import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Eye, Search, Sparkles, Globe2, Loader2, ExternalLink, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useI18n } from "@/lib/i18n";
import {
  searchCompetitorAds,
  adDurationDays,
  isLongRunning,
  type CompetitorAd,
  type CompetitorSearchResult,
  type AdPlatform,
} from "@/lib/competitor-data";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/competitors")({
  component: CompetitorsPage,
  head: () => ({
    meta: [
      { title: "Competitor Research · AdKavi" },
      {
        name: "description",
        content:
          "Spy on competitor ads, see how long each has been running, and get an AI plan to outperform them.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type Insight = {
  summary: string;
  workingPatterns: string[];
  weaknesses: string[];
  howToBeatThem: string[];
  suggestedHooks: string[];
};

const PLATFORM_META: Record<AdPlatform, { label: string; color: string }> = {
  meta: { label: "Meta", color: "#1877F2" },
  instagram: { label: "Instagram", color: "#E4405F" },
  tiktok: { label: "TikTok", color: "#FE2C55" },
  google: { label: "Google", color: "#4285F4" },
  youtube: { label: "YouTube", color: "#FF0000" },
};

function CompetitorsPage() {
  const { t, lang } = useI18n();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CompetitorSearchResult | null>(null);
  const [insight, setInsight] = useState<Insight | null>(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState<string | null>(null);

  const onSearch = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const q = query.trim();
    if (!q) {
      toast.error(t("competitorsPage.error.emptyQuery"));
      return;
    }
    setLoading(true);
    setInsight(null);
    setInsightError(null);
    try {
      const r = await searchCompetitorAds(q);
      setResult(r);
    } catch (err) {
      console.error(err);
      toast.error(t("competitorsPage.error.searchFailed"));
    } finally {
      setLoading(false);
    }
  };

  const onGenerateInsight = async () => {
    if (!result) return;
    setInsightLoading(true);
    setInsightError(null);
    try {
      const { data, error } = await supabase.functions.invoke("competitor-insights", {
        body: {
          competitor: result.competitor,
          language: lang,
          ads: result.ads.map((a) => ({
            platform: a.platform,
            format: a.format,
            headline: a.headline,
            body: a.body,
            cta: a.cta,
            durationDays: adDurationDays(a),
          })),
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setInsight(data.insight as Insight);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("competitorsPage.error.insightFailed");
      setInsightError(msg);
    } finally {
      setInsightLoading(false);
    }
  };

  return (
    <section className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-3">
          <Eye className="w-3.5 h-3.5" /> {t("competitorsPage.eyebrow")}
        </div>
        <h1 className="text-3xl lg:text-4xl font-bold tracking-tight flex items-center gap-3">
          <Eye className="w-8 h-8 text-orange-400 shrink-0" />
          <span className="bg-gradient-to-r from-orange-400 to-primary bg-clip-text text-transparent">{t("competitorsPage.title")}</span>
        </h1>
        <p className="mt-2 text-muted-foreground max-w-2xl">{t("competitorsPage.subtitle")}</p>
      </header>

      <form onSubmit={onSearch} className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("competitorsPage.searchPlaceholder")}
            className="pl-9"
            aria-label={t("competitorsPage.searchPlaceholder")}
            maxLength={120}
          />
        </div>
        <Button type="submit" disabled={loading} className="shrink-0">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {t("competitorsPage.searchCta")}
        </Button>
      </form>

      {result?.isMock && (
        <Alert className="mb-6 border-amber-500/30 bg-amber-500/5">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t("competitorsPage.mockBadge")}</AlertTitle>
          <AlertDescription>{t("competitorsPage.mockDesc")}</AlertDescription>
        </Alert>
      )}

      {!result && !loading && <EmptyState />}

      {result && (
        <div className="space-y-8">
          <PlatformSpy result={result} />
          <CompetitorAdsGrid result={result} />
          <AiInsightCard
            hasResult={!!result}
            loading={insightLoading}
            insight={insight}
            error={insightError}
            onGenerate={onGenerateInsight}
          />
        </div>
      )}
    </section>
  );
}

function EmptyState() {
  const { t } = useI18n();
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card/50 p-10 text-center">
      <Eye className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
      <h2 className="text-lg font-semibold">{t("competitorsPage.empty.title")}</h2>
      <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
        {t("competitorsPage.empty.desc")}
      </p>
    </div>
  );
}

function PlatformSpy({ result }: { result: CompetitorSearchResult }) {
  const { t } = useI18n();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe2 className="w-4 h-4" /> {t("competitorsPage.platformSpy.title")}
        </CardTitle>
        <CardDescription>
          {t("competitorsPage.platformSpy.desc", { brand: result.competitor })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(PLATFORM_META) as AdPlatform[]).map((p) => {
            const active = result.platforms.includes(p);
            const meta = PLATFORM_META[p];
            return (
              <div
                key={p}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm ${
                  active
                    ? "bg-card border-border text-foreground"
                    : "bg-muted/30 border-border/50 text-muted-foreground line-through"
                }`}
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: active ? meta.color : "currentColor" }}
                />
                {meta.label}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function CompetitorAdsGrid({ result }: { result: CompetitorSearchResult }) {
  const { t } = useI18n();
  const longRunningCount = useMemo(
    () => result.ads.filter((a) => isLongRunning(a)).length,
    [result.ads],
  );
  return (
    <div>
      <div className="flex items-end justify-between gap-4 mb-4">
        <div>
          <h2 className="text-xl font-semibold">
            {t("competitorsPage.ads.title", { brand: result.competitor })}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("competitorsPage.ads.summary", {
              count: result.ads.length,
              long: longRunningCount,
            })}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {result.ads.map((ad) => (
          <AdCard key={ad.id} ad={ad} />
        ))}
      </div>
    </div>
  );
}

function AdCard({ ad }: { ad: CompetitorAd }) {
  const { t } = useI18n();
  const days = adDurationDays(ad);
  const longRunning = isLongRunning(ad);
  const platform = PLATFORM_META[ad.platform];
  return (
    <article className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col">
      <div
        className="h-32 flex items-center justify-center text-xs uppercase tracking-wide font-medium text-white"
        style={{
          background: `linear-gradient(135deg, ${platform.color}, color-mix(in oklab, ${platform.color} 60%, black))`,
        }}
      >
        {ad.format}
      </div>
      <div className="p-4 flex-1 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: platform.color }} />
            <span className="text-xs font-medium">{platform.label}</span>
          </div>
          {longRunning ? (
            <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/30">
              {t("competitorsPage.ads.longBadge")}
            </Badge>
          ) : (
            <Badge variant="outline">{t("competitorsPage.ads.testingBadge")}</Badge>
          )}
        </div>
        <h3 className="font-semibold leading-snug line-clamp-2">{ad.headline}</h3>
        <p className="text-sm text-muted-foreground line-clamp-3">{ad.body}</p>
        <div className="mt-auto pt-2 flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {t("competitorsPage.ads.runningFor", { days })}
          </span>
          {ad.landingDomain && (
            <span className="inline-flex items-center gap-1 truncate max-w-[50%]">
              <ExternalLink className="w-3 h-3 shrink-0" />
              <span className="truncate">{ad.landingDomain}</span>
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

function AiInsightCard({
  hasResult,
  loading,
  insight,
  error,
  onGenerate,
}: {
  hasResult: boolean;
  loading: boolean;
  insight: Insight | null;
  error: string | null;
  onGenerate: () => void;
}) {
  const { t } = useI18n();
  return (
    <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" /> {t("competitorsPage.insight.title")}
        </CardTitle>
        <CardDescription>{t("competitorsPage.insight.desc")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!insight && (
          <Button onClick={onGenerate} disabled={!hasResult || loading}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> {t("competitorsPage.insight.loading")}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" /> {t("competitorsPage.insight.cta")}
              </>
            )}
          </Button>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {insight && (
          <div className="space-y-5">
            <p className="text-sm">{insight.summary}</p>
            <InsightSection title={t("competitorsPage.insight.working")} items={insight.workingPatterns} />
            <InsightSection title={t("competitorsPage.insight.weak")} items={insight.weaknesses} />
            <InsightSection title={t("competitorsPage.insight.beat")} items={insight.howToBeatThem} />
            <InsightSection title={t("competitorsPage.insight.hooks")} items={insight.suggestedHooks} />
            <Button variant="outline" size="sm" onClick={onGenerate} disabled={loading}>
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {t("competitorsPage.insight.regenerate")}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function InsightSection({ title, items }: { title: string; items: string[] }) {
  if (!items?.length) return null;
  return (
    <div>
      <h4 className="text-sm font-semibold mb-2">{title}</h4>
      <ul className="space-y-1.5 text-sm text-muted-foreground">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-primary mt-1.5 w-1 h-1 rounded-full bg-primary shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
